import { cn, itemPhaseDot, MonoLabel } from '@/design-system';

import { surveyCopy } from '../copy';
import type { NodeId } from '../engine';
import type { TreeRow } from '../model/view';

export interface SurveyTreeProps {
    rows: readonly TreeRow[];
    currentNodeId: NodeId | null;
    onSelect: (nodeId: NodeId) => void;
    className?: string;
}

/**
 * De boom links. Sectiekoppen tonen het normnummer, de naam en `klaar/totaal`
 * onderdelen; onderdelen zijn rijen van 42px met een statusstip en
 * `beantwoord/zichtbaar`. Tellers tellen uitsluitend zichtbare vragen - dat
 * rekent de engine, niet dit scherm. Stelt een onderdeel zelf (nog) niets, omdat
 * zijn pad op een vraag van een eerder onderdeel wacht of daar eindigt, dan zegt
 * alleen de stip iets: "0/0" leest als een fout.
 */
export function SurveyTree({ rows, currentNodeId, onSelect, className }: SurveyTreeProps) {
    return (
        <nav
            aria-label={surveyCopy.tree.title}
            className={cn(
                'overflow-auto border-r border-[var(--border-default)] bg-[var(--surface-card)] px-[10px] py-3',
                className,
            )}
        >
            <MonoLabel size={10} as="div" className="px-2 py-[6px]">
                {surveyCopy.tree.title}
            </MonoLabel>

            {rows.length === 0 ? (
                <p className="px-2 py-3 text-[12px] text-[var(--text-tertiary-safe)]">
                    {surveyCopy.tree.empty}
                </p>
            ) : null}

            {rows.map((row) =>
                row.isSection ? (
                    <div
                        key={row.nodeId}
                        className="mt-2 flex items-center gap-2 p-2 text-[12px] leading-[1.25] font-bold text-[var(--text-secondary)]"
                    >
                        <MonoLabel size={10} variant="value">
                            {row.number}
                        </MonoLabel>
                        <span className="flex-1 leading-[1.25]">{row.name}</span>
                        <MonoLabel size={10} variant="value">
                            {row.childrenDone}/{row.childrenTotal}
                        </MonoLabel>
                    </div>
                ) : (
                    <button
                        key={row.nodeId}
                        type="button"
                        aria-current={row.nodeId === currentNodeId ? 'true' : undefined}
                        onClick={() => onSelect(row.nodeId)}
                        style={{ paddingInlineStart: 8 + Math.max(0, row.depth - 1) * 10 }}
                        className={cn(
                            'flex min-h-[42px] w-full cursor-pointer items-center gap-[9px] rounded-[7px] border-0 py-[10px] pr-2 text-left text-[13px]',
                            'font-[family-name:var(--font-body)] outline-offset-2 focus-visible:outline-2 focus-visible:outline-[var(--accent)]',
                            row.nodeId === currentNodeId
                                ? 'bg-[var(--accent-tint)] font-medium text-[var(--accent-dark)]'
                                : 'bg-transparent font-normal text-[var(--text-reading)] hover:bg-[var(--surface-row-hover)]',
                        )}
                    >
                        <span
                            aria-hidden="true"
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ background: itemPhaseDot[row.phase] }}
                        />
                        <span className="flex-1 leading-[1.3]">{row.name}</span>
                        {row.visible > 0 ? (
                            <MonoLabel
                                size={10}
                                variant="value"
                                aria-label={surveyCopy.tree.itemCount(row.answered, row.visible)}
                            >
                                {row.answered}/{row.visible}
                            </MonoLabel>
                        ) : null}
                    </button>
                ),
            )}
        </nav>
    );
}
